import { useState, useMemo, useCallback, Fragment } from "react";
import useWalletTransactionList from "../../../Hooks/storageRoles/useWalletTransactionList";
import {
  PageShell,
  PageHeader,
  ListSkeleton,
  EmptyState,
  Drawer,
  ListView,
  ListToolbar,
} from "../../../Components/shared";
import SearchDateFilters from "../../../Components/wallet/SearchDateFilters";
import { Pagination } from "../../../Components/atoms";
import { FaShoppingCart, FaLayerGroup, FaUndo } from "react-icons/fa";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";
import {
  UserCell,
  TypeBadge,
  StatusBadge,
  AmountText,
  formatDate,
  formatTL,
} from "../../../Components/wallet/walletTxUi";
import MachineRecordSection from "../../../Components/wallet/MachineRecordSection";

const PAYMENT_METHOD_LABEL = {
  credit_card: "Kredi Kartı",
  debit_card: "Banka Kartı",
  app_personal_card: "BayinerPay NFC",
  app_qr: "BayinerPay QR",
};

function DetailRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span className="text-sm text-gray-800 font-medium text-right break-words">
        {value}
      </span>
    </div>
  );
}

function SaleDetailDrawer({ tx, onClose }) {
  const isRefund = tx?.txType === "refund";
  return (
    <Drawer
      open={!!tx}
      onClose={onClose}
      width="md"
      title={isRefund ? "İade Detayı" : "Satış Detayı"}
      subtitle={tx?.reference || ""}
      titleIcon={
        isRefund ? (
          <FaUndo className="w-5 h-5 text-main" />
        ) : (
          <FaShoppingCart className="w-5 h-5 text-main" />
        )
      }
    >
      {tx && (
        <Drawer.Body className="p-5 space-y-5">
          {/* Üst: tutar + rozetler */}
          <div className="flex items-center justify-between">
            <AmountText tx={tx} className="text-2xl" />
            <div className="flex items-center gap-1.5">
              <TypeBadge txType={tx.txType} />
              <StatusBadge status={tx.status} />
            </div>
          </div>

          {/* Kullanıcı */}
          <div className="bg-gray-50 rounded-xl p-3">
            <UserCell user={tx.user} />
          </div>

          {/* Ödeme bilgileri */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Ödeme
            </h4>
            <DetailRow
              label="Ödeme Yöntemi"
              value={PAYMENT_METHOD_LABEL[tx.paymentMethod] || tx.paymentMethod}
            />
            <DetailRow
              label="Gateway"
              value={(tx.merchant || tx.gateway || "").toUpperCase()}
            />
            <DetailRow label="Cihaz (Seri No)" value={tx.device?.serialNo} />
            <DetailRow label="Tarih" value={formatDate(tx.date)} />
          </div>

          {/* Referans */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Referans
            </h4>
            <DetailRow label="Fatura No" value={tx.reference} />
            <DetailRow label="Intent ID" value={tx.intentId} />
          </div>

          {/* Ürünler */}
          {Array.isArray(tx.items) && tx.items.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Ürünler
              </h4>
              <div className="space-y-1.5">
                {tx.items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-gray-800 font-medium truncate">
                        {item.name || item.productName || "Ürün"}
                      </div>
                      {(item.volume || item.vessel) && (
                        <div className="text-xs text-gray-500 truncate">
                          {[item.volume, item.vessel].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    {item.price != null && (
                      <span className="text-sm font-semibold text-gray-700 shrink-0 ml-2">
                        {formatTL(Number(item.price) / 100)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sebep */}
          {tx.reason && (tx.reason.code || tx.reason.description) && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Sonuç
              </h4>
              <DetailRow label="Kod" value={tx.reason.code} />
              <DetailRow label="Açıklama" value={tx.reason.description} />
            </div>
          )}

          {/* Makinenin kendi kaydı (#243) — ayrı sekmeye gitmeden */}
          <MachineRecordSection intentId={tx.intentId} reference={tx.reference} />
        </Drawer.Body>
      )}
    </Drawer>
  );
}

// İşlem türü sekmeleri — sunucuda süzülür (#242); Tümü = satış + iade
const FILTERS = [
  { id: "all", label: "Tümü", icon: FaLayerGroup },
  { id: "payment", label: "Satış", icon: FaShoppingCart },
  { id: "refund", label: "İade", icon: FaUndo },
];

const TABLE_COLUMNS = [
  { label: "Fatura No", width: "w-40", skeletonWidth: "w-28" },
  { label: "Kullanıcı", width: "", skeletonWidth: "w-32", skeletonExtra: true },
  { label: "Tür", width: "w-24", skeletonWidth: "w-16" },
  { label: "Tutar", width: "w-28", skeletonWidth: "w-16" },
  { label: "Ödeme Yöntemi", width: "w-36 whitespace-nowrap", skeletonWidth: "w-14" },
  { label: "Durum", width: "w-28", skeletonWidth: "w-16" },
  { label: "Tarih", width: "w-40", skeletonWidth: "w-24" },
];

// Raporlardaki DatePicker görünümüyle aynı stil


// `embedded`: PaymentsPage sekmesi olarak render edilirken kendi PageShell'ini
// KURMAZ (kabuk ust sayfada). Kendi PageHeader/stat blogu sekme icinde kalir.
const toParams = (filter) => ({ type: filter === "all" ? "sales" : filter });

function SalesPage({ embedded = false }) {
  const [selectedTx, setSelectedTx] = useState(null);
  const {
    transactions: sales,
    total,
    totalPages,
    counts,
    isLoading,
    isFetching,
    activeFilter,
    setActiveFilter,
    search,
    setSearch,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    currentPage,
    setCurrentPage,
    pageSize,
    clear,
  } = useWalletTransactionList({ toParams });

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      const tableContainer = document.querySelector(".overflow-y-auto");
      if (tableContainer) tableContainer.scrollTop = 0;
    },
    [setCurrentPage]
  );

  // Sayaçlar sunucudan: arama + tarih uygulanmış, sekmeden bağımsız.
  // Eski sunucu `counts` göndermez → 0 yerine hiç gösterme
  const stats = useMemo(
    () => counts && [
      { icon: FaLayerGroup, value: counts?.all ?? 0, label: "Toplam", color: "blue" },
      { icon: FaShoppingCart, value: counts?.payment ?? 0, label: "Satış", color: "emerald" },
      { icon: FaUndo, value: counts?.refund ?? 0, label: "İade", color: "amber" },
    ],
    [counts]
  );

  // Tarih aralığı — raporlardaki tarih seçicilerle aynı görünüm.
  // ListToolbar `search` node'u: masaüstünde arama satırında, mobilde filtre
  // modalının içinde görünür.
  const dateRangeNode = (
    <SearchDateFilters
      search={search}
      onSearchChange={setSearch}
      placeholder="Fatura No, Intent ID, kullanıcı, seri no"
      startDate={startDate}
      setStartDate={setStartDate}
      endDate={endDate}
      setEndDate={setEndDate}
    />
  );

  const Shell = embedded ? Fragment : PageShell;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
      <Shell>
        {/* Sekme olarak gomuluyken baslik ustte (PaymentsPage) tek sefer
            durur; burada ayri bir baslik satiri olmaz ve istatistik rozetleri
            asagida filtre butonlariyla AYNI satira gecer. */}
        {!embedded && (
          <div className="px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 shrink-0">
            <PageHeader
              icon={FaShoppingCart}
              iconColor="emerald"
              title="Satış"
              subtitle="Satış ve iade işlemleri"
              stats={stats}
              isLoading={isLoading}
              mobileSlotId="sales-toolbar-mobile-slot"
            />
          </div>
        )}

        <div className="bg-white flex-1 min-h-0 flex flex-col">
          <ListToolbar
            className={
              embedded
                ? "px-3 pt-2.5 pb-2.5 sm:px-4 sm:pt-4 sm:pb-4 md:px-6 md:pt-4 md:pb-6"
                : "px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
            }
            mobileFilterSlotId="sales-toolbar-mobile-slot"
            stats={embedded ? stats : undefined}
            filterTabs={{
              filters: FILTERS,
              activeFilter,
              onChange: setActiveFilter,
              totalCount: total,
              isLoading: isFetching,
              color: "emerald",
            }}
            search={dateRangeNode}
            onClear={clear}
          />

          <ListView
            headers={TABLE_COLUMNS.map((c) => ({ label: c.label, className: c.width }))}
            data={sales}
            rowKey={(tx) => `${tx.txType}-${tx._id}`}
            renderRow={(tx) => (
              <tr
                key={`${tx.txType}-${tx._id}`}
                className="bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelectedTx(tx)}
              >
                <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                  {tx.reference || "-"}
                </td>
                <td className="px-4 py-3">
                  <UserCell user={tx.user} />
                </td>
                <td className="px-4 py-3">
                  <TypeBadge txType={tx.txType} />
                </td>
                <td className="px-4 py-3 text-sm">
                  <AmountText tx={tx} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 uppercase">
                  {tx.gateway || "-"}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={tx.status} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {formatDate(tx.date)}
                </td>
              </tr>
            )}
            renderMobileCard={(tx) => (
              <div
                className="bg-white px-3.5 py-3 border-b border-gray-200 active:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedTx(tx)}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <AmountText tx={tx} className="text-sm" />
                  <div className="flex items-center gap-1.5">
                    <TypeBadge txType={tx.txType} />
                    <StatusBadge status={tx.status} />
                  </div>
                </div>
                <UserCell user={tx.user} compact />
                <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                  <span className="font-mono truncate">{tx.reference || "-"}</span>
                  <span>{formatDate(tx.date)}</span>
                </div>
              </div>
            )}
            isLoading={isFetching}
            loadingNode={
              <ListSkeleton columns={TABLE_COLUMNS} rowCount={10} mobileCardCount={6} />
            }
            empty={
              <div className="p-4 md:p-6">
                <EmptyState
                  message="Satış/iade kaydı bulunamadı"
                  suggestion="Filtreleri değiştirin veya daha sonra tekrar deneyin."
                />
              </div>
            }
            footer={
              totalPages > 1 ? (
                <div className="shrink-0 border-t border-gray-200 bg-white">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={pageSize}
                    totalItems={total}
                    showInfo={true}
                    disabled={isFetching}
                  />
                </div>
              ) : null
            }
          />
        </div>

        <SaleDetailDrawer tx={selectedTx} onClose={() => setSelectedTx(null)} />
      </Shell>
    </LocalizationProvider>
  );
}

export default SalesPage;
